"use client"

import {
  Building2,
  Globe,
  ImageIcon,
  LinkIcon,
  MapPin,
  Phone,
  Tag,
} from "lucide-react"
import type { FormEvent } from "react"
import { useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { CardContent, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"

export interface Neighborhood {
  id: number
  name: string
}

export interface BusinessFormData {
  business_name: string
  business_type: "restaurant" | "store" | "service" | "delivery"
  description: string
  phone: string
  email: string
  website?: string
  address: string
  latitude?: string
  longitude?: string
  neighborhood_id?: string
  opening_hours?: string
  delivery_coverage?: string
  images?: File[]
  image_urls?: string[]
  images_to_delete?: (number | string)[]
}

export interface BusinessFormProps {
  title: string
  data: BusinessFormData
  errors: Record<string, string>
  neighborhoods: Neighborhood[]
  processing?: boolean
  existingImages?: { id: number | string; url: string }[]
  onChange: (partial: Partial<BusinessFormData>) => void
  onSubmit: (e: FormEvent) => void
  onCancel: () => void
  submitLabel?: string
}

const businessTypes = [
  { value: "restaurant", label: "Restaurante" },
  { value: "store", label: "Tienda" },
  { value: "service", label: "Servicio" },
  { value: "delivery", label: "Delivery" },
]

export function BusinessForm({
  title,
  data,
  errors,
  neighborhoods,
  processing = false,
  existingImages = [],
  onChange,
  onSubmit,
  onCancel,
  submitLabel = "Guardar",
}: BusinessFormProps) {
  const err = errors
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [newPreviews, setNewPreviews] = useState<{ url: string; file: File }[]>(
    [],
  )
  const [urlInput, setUrlInput] = useState("")
  const [imagesToDelete, setImagesToDelete] = useState<Set<number | string>>(
    new Set(),
  )

  const toggleImageForDeletion = (imageId: number | string) => {
    const newSet = new Set(imagesToDelete)
    if (newSet.has(imageId)) {
      newSet.delete(imageId)
    } else {
      newSet.add(imageId)
    }
    setImagesToDelete(newSet)
    onChange({ images_to_delete: Array.from(newSet) })
  }

  const labelFor: Record<string, string> = {
    business_name: "Nombre del negocio",
    business_type: "Tipo de negocio",
    description: "Descripción",
    address: "Dirección",
    neighborhood_id: "Barrio",
    latitude: "Latitud",
    longitude: "Longitud",
    phone: "Teléfono",
    email: "Email",
    website: "Website",
    opening_hours: "Horarios de apertura",
    delivery_coverage: "Cobertura de delivery",
    images: "Imágenes",
    image_urls: "URLs de imágenes",
  }
  const [imageMode, setImageMode] = useState<"file" | "url">("file")

  // Opening hours structured state
  type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"
  type OpeningHours = Record<
    DayKey,
    { open: boolean; start: string; end: string }
  >
  const dayOrder: { key: DayKey; label: string }[] = [
    { key: "mon", label: "Lun" },
    { key: "tue", label: "Mar" },
    { key: "wed", label: "Mié" },
    { key: "thu", label: "Jue" },
    { key: "fri", label: "Vie" },
    { key: "sat", label: "Sáb" },
    { key: "sun", label: "Dom" },
  ]
  const defaultHours: OpeningHours = {
    mon: { open: true, start: "09:00", end: "18:00" },
    tue: { open: true, start: "09:00", end: "18:00" },
    wed: { open: true, start: "09:00", end: "18:00" },
    thu: { open: true, start: "09:00", end: "18:00" },
    fri: { open: true, start: "09:00", end: "18:00" },
    sat: { open: false, start: "09:00", end: "13:00" },
    sun: { open: false, start: "09:00", end: "13:00" },
  }
  const parseOpeningHours = (value?: string): OpeningHours => {
    if (!value) return defaultHours
    try {
      const raw = JSON.parse(value) as unknown
      if (typeof raw !== "object" || raw === null) return defaultHours
      type DayPartial = Partial<{ open: boolean; start: string; end: string }>
      const p = raw as Partial<Record<DayKey, DayPartial>>
      return {
        mon: {
          open: !!p.mon?.open,
          start: p.mon?.start ?? "09:00",
          end: p.mon?.end ?? "18:00",
        },
        tue: {
          open: !!p.tue?.open,
          start: p.tue?.start ?? "09:00",
          end: p.tue?.end ?? "18:00",
        },
        wed: {
          open: !!p.wed?.open,
          start: p.wed?.start ?? "09:00",
          end: p.wed?.end ?? "18:00",
        },
        thu: {
          open: !!p.thu?.open,
          start: p.thu?.start ?? "09:00",
          end: p.thu?.end ?? "18:00",
        },
        fri: {
          open: !!p.fri?.open,
          start: p.fri?.start ?? "09:00",
          end: p.fri?.end ?? "18:00",
        },
        sat: {
          open: !!p.sat?.open,
          start: p.sat?.start ?? "09:00",
          end: p.sat?.end ?? "13:00",
        },
        sun: {
          open: !!p.sun?.open,
          start: p.sun?.start ?? "09:00",
          end: p.sun?.end ?? "13:00",
        },
      }
    } catch {
      return defaultHours
    }
  }
  const [openingHours, setOpeningHours] = useState<OpeningHours>(() =>
    parseOpeningHours(data.opening_hours),
  )
  const syncOpeningHours = (next: OpeningHours) => {
    setOpeningHours(next)
    onChange({ opening_hours: JSON.stringify(next) })
  }

  // Coverage structured state
  type CoverageMode = "neighborhoods" | "radius"
  type CoverageParsed =
    | { type: "neighborhoods"; neighborhoods: (string | number)[] }
    | { type: "radius"; km: number }
    | null
  const parseCoverage = (value?: string): CoverageParsed => {
    if (!value) return null
    try {
      const raw = JSON.parse(value) as unknown
      if (typeof raw !== "object" || raw === null) return null
      const r = raw as Partial<Record<string, unknown>>
      if (r.type === "radius" && typeof r.km === "number") {
        return { type: "radius", km: r.km }
      }
      if (r.type === "neighborhoods" && Array.isArray(r.neighborhoods)) {
        return {
          type: "neighborhoods",
          neighborhoods: r.neighborhoods as (string | number)[],
        }
      }
      return null
    } catch {
      return null
    }
  }
  const initialCoverage = parseCoverage(data.delivery_coverage)
  const [coverageMode, setCoverageMode] = useState<CoverageMode>(
    initialCoverage?.type ?? "neighborhoods",
  )
  const [coverageNeighborhoods, setCoverageNeighborhoods] = useState<string[]>(
    initialCoverage?.type === "neighborhoods"
      ? initialCoverage.neighborhoods.map((id) => String(id))
      : [],
  )
  const [coverageRadiusKm, setCoverageRadiusKm] = useState<string>(
    initialCoverage?.type === "radius" ? String(initialCoverage.km) : "3",
  )
  const syncCoverage = (mode: CoverageMode, nbhds: string[], km: string) => {
    const payload =
      mode === "neighborhoods"
        ? { type: "neighborhoods", neighborhoods: nbhds }
        : { type: "radius", km: Number(km) || 0 }
    onChange({ delivery_coverage: JSON.stringify(payload) })
  }

  return (
    <div className="w-full max-w-4xl">
      <CardTitle className="text-foreground flex items-center gap-3 px-4 text-2xl font-bold">
        <Building2 className="text-primary h-8 w-8" />
        {title}
      </CardTitle>
      <CardContent className="px-4">
        {Object.keys(err).length > 0 && (
          <div className="alert-error mb-6">
            <div className="mb-2 flex items-center gap-2">
              <div className="bg-status-error h-2 w-2 rounded-full"></div>
              <p className="text-destructive font-semibold">
                Por favor corrige los siguientes errores:
              </p>
            </div>
            <ul className="text-destructive list-disc space-y-1 pl-6 text-sm">
              {Object.entries(err).map(([k, v]) => (
                <li key={k}>
                  <span className="font-medium">{labelFor[k] ?? k}:</span> {v}
                </li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Nombre del negocio
              </Label>
              <Input
                value={data.business_name}
                onChange={(e) => onChange({ business_name: e.target.value })}
                required
                aria-invalid={Boolean(err.business_name) || undefined}
                className={`h-12 border-2 text-base transition-colors duration-200 ${err.business_name ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
              />
              {err.business_name && (
                <p className="text-destructive mt-1 text-sm">
                  {err.business_name}
                </p>
              )}
            </div>
            <div>
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Tipo de negocio
              </Label>
              <Select
                value={data.business_type}
                onValueChange={(v) =>
                  onChange({
                    business_type: v as BusinessFormData["business_type"],
                  })
                }
              >
                <SelectTrigger
                  className={`h-12 border-2 px-3 py-5 ${err.business_type ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                  aria-invalid={Boolean(err.business_type) || undefined}
                >
                  <SelectValue placeholder="Selecciona un tipo" />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {err.business_type && (
                <p className="text-destructive mt-1 text-sm">
                  {err.business_type}
                </p>
              )}
            </div>
            <div className="md:col-span-2">
              <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                Descripción
              </Label>
              <Textarea
                value={data.description}
                onChange={(e) => onChange({ description: e.target.value })}
                rows={4}
                aria-invalid={Boolean(err.description) || undefined}
                className={`resize-none border-2 text-base transition-colors duration-200 ${err.description ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
              />
              {err.description && (
                <p className="text-destructive mt-1 text-sm">
                  {err.description}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <MapPin className="text-primary h-5 w-5" />
                Ubicación
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Dirección
                </Label>
                <Input
                  value={data.address}
                  onChange={(e) => onChange({ address: e.target.value })}
                  aria-invalid={Boolean(err.address) || undefined}
                  className={`h-12 border-2 text-base transition-colors duration-200 ${err.address ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                />
                {err.address && (
                  <p className="text-destructive mt-1 text-sm">{err.address}</p>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Barrio
                </Label>
                <Select
                  value={data.neighborhood_id ?? ""}
                  onValueChange={(v) => onChange({ neighborhood_id: v })}
                >
                  <SelectTrigger
                    className={`h-12 border-2 px-3 py-5 ${err.neighborhood_id ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                    aria-invalid={Boolean(err.neighborhood_id) || undefined}
                  >
                    <SelectValue placeholder="Selecciona un barrio" />
                  </SelectTrigger>
                  <SelectContent>
                    {neighborhoods.map((n) => (
                      <SelectItem key={n.id} value={String(n.id)}>
                        {n.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {err.neighborhood_id && (
                  <p className="text-destructive mt-1 text-sm">
                    {err.neighborhood_id}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Latitud
                </Label>
                <Input
                  value={data.latitude ?? ""}
                  onChange={(e) => onChange({ latitude: e.target.value })}
                  aria-invalid={Boolean(err.latitude) || undefined}
                  className={`h-12 border-2 text-base transition-colors duration-200 ${err.latitude ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                />
                {err.latitude && (
                  <p className="text-destructive mt-1 text-sm">
                    {err.latitude}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Longitud
                </Label>
                <Input
                  value={data.longitude ?? ""}
                  onChange={(e) => onChange({ longitude: e.target.value })}
                  aria-invalid={Boolean(err.longitude) || undefined}
                  className={`h-12 border-2 text-base transition-colors duration-200 ${err.longitude ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                />
                {err.longitude && (
                  <p className="text-destructive mt-1 text-sm">
                    {err.longitude}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-secondary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <Tag className="text-secondary h-5 w-5" />
                Contacto
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Teléfono
                </Label>
                <div className="relative">
                  <Phone className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    value={data.phone}
                    onChange={(e) => onChange({ phone: e.target.value })}
                    aria-invalid={Boolean(err.phone) || undefined}
                    className={`h-12 border-2 pl-9 text-base transition-colors duration-200 ${err.phone ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                  />
                </div>
                {err.phone && (
                  <p className="text-destructive mt-1 text-sm">{err.phone}</p>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Email
                </Label>
                <Input
                  type="email"
                  value={data.email}
                  onChange={(e) => onChange({ email: e.target.value })}
                  aria-invalid={Boolean(err.email) || undefined}
                  className={`h-12 border-2 text-base transition-colors duration-200 ${err.email ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                />
                {err.email && (
                  <p className="text-destructive mt-1 text-sm">{err.email}</p>
                )}
              </div>
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Website (opcional)
                </Label>
                <div className="relative">
                  <Globe className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                  <Input
                    value={data.website ?? ""}
                    onChange={(e) => onChange({ website: e.target.value })}
                    aria-invalid={Boolean(err.website) || undefined}
                    className={`h-12 border-2 pl-9 text-base transition-colors duration-200 ${err.website ? "border-destructive focus:border-destructive" : "border-border focus:border-primary"}`}
                  />
                </div>
                {err.website && (
                  <p className="text-destructive mt-1 text-sm">{err.website}</p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-accent h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <ImageIcon className="text-primary h-5 w-5" />
                Imágenes
              </h3>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant={imageMode === "file" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("file")}
              >
                <ImageIcon className="mr-2 h-4 w-4" /> Subir archivos
              </Button>
              <Button
                type="button"
                variant={imageMode === "url" ? "default" : "outline"}
                size="sm"
                onClick={() => setImageMode("url")}
              >
                <LinkIcon className="mr-2 h-4 w-4" /> Usar URLs
              </Button>
            </div>
            {imageMode === "file" && (
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = e.target.files ? Array.from(e.target.files) : []
                  if (files.length === 0) return
                  const mergedFiles = [...(data.images ?? []), ...files]
                  onChange({ images: mergedFiles })
                  const previews = files.map((f) => ({
                    url: URL.createObjectURL(f),
                    file: f,
                  }))
                  setNewPreviews((prev) => [...prev, ...previews])
                  if (fileInputRef.current) fileInputRef.current.value = ""
                }}
                className="border-muted file:bg-accent/10 file:text-primary hover:file:bg-accent/20 h-12 w-full border-2 border-dashed file:mr-4 file:truncate file:rounded-lg file:border-0 file:px-4 file:py-2 file:text-sm file:font-medium"
              />
            )}

            {imageMode === "url" && (
              <div className="mt-3 flex gap-2">
                <Input
                  type="url"
                  placeholder="Pega una URL de imagen y presiona Enter o Agregar"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      const v = urlInput.trim()
                      if (!v) return
                      const next = [...(data.image_urls ?? []), v]
                      onChange({ image_urls: next })
                      setUrlInput("")
                    }
                  }}
                  className="h-12 text-base"
                />
                <Button
                  type="button"
                  onClick={() => {
                    const v = urlInput.trim()
                    if (!v) return
                    const next = [...(data.image_urls ?? []), v]
                    onChange({ image_urls: next })
                    setUrlInput("")
                  }}
                >
                  Agregar
                </Button>
              </div>
            )}

            {(Array.isArray(existingImages) && existingImages.length > 0) ||
            newPreviews.length > 0 ||
            (data.image_urls && data.image_urls.length > 0) ? (
              <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
                {existingImages.map(
                  (img: { id: number | string; url: string }) => {
                    const isMarkedForDeletion = imagesToDelete.has(img.id)
                    if (isMarkedForDeletion) return null
                    return (
                      <div
                        key={`ex-${String(img.id)}`}
                        className="border-border bg-muted hover:border-primary relative aspect-square overflow-hidden rounded-lg border-2 transition-all"
                      >
                        <img
                          src={img.url}
                          alt="Imagen existente"
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => toggleImageForDeletion(img.id)}
                          className="hover:bg-destructive absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-xs font-bold text-white"
                          title="Eliminar imagen"
                        >
                          ✕
                        </button>
                      </div>
                    )
                  },
                )}
                {newPreviews.map((p, idx) => (
                  <div
                    key={`new-${idx}`}
                    className="border-border bg-muted relative aspect-square overflow-hidden rounded-lg border-2"
                  >
                    <img
                      src={p.url}
                      alt="Nueva imagen"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      className="hover:bg-destructive absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-xs font-bold text-white"
                      title="Quitar esta imagen"
                      onClick={() => {
                        URL.revokeObjectURL(p.url)
                        setNewPreviews((prev) =>
                          prev.filter((_, i) => i !== idx),
                        )
                        const remaining = (data.images ?? []).filter(
                          (f) => f !== p.file,
                        )
                        onChange({ images: remaining })
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {(data.image_urls ?? []).map((u: string, idx: number) => (
                  <div
                    key={`url-${idx}-${u}`}
                    className="border-border bg-muted relative aspect-square overflow-hidden rounded-lg border-2"
                  >
                    <img
                      src={u}
                      alt="Imagen por URL"
                      className="h-full w-full object-cover"
                    />
                    <button
                      type="button"
                      className="hover:bg-destructive absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/50 text-xs font-bold text-white"
                      title="Quitar esta URL"
                      onClick={() => {
                        const next = (data.image_urls ?? []).filter(
                          (_, i) => i !== idx,
                        )
                        onChange({ image_urls: next })
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            {imageMode === "url" && err.image_urls && (
              <p className="text-destructive mt-1 text-sm">{err.image_urls}</p>
            )}
          </div>

          <div className="space-y-4">
            <div className="mb-4 flex items-center gap-3">
              <div
                className="bg-primary h-6 w-1 rounded"
                aria-hidden="true"
              ></div>
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                <LinkIcon className="text-primary h-5 w-5" />
                Horarios y Cobertura
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {/* Opening Hours Editor */}
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Horarios de apertura
                </Label>
                <div className="space-y-3">
                  <div className="space-y-2">
                    {dayOrder.map(({ key, label }) => (
                      <div
                        key={key}
                        className="grid grid-cols-[64px,auto] items-center gap-3"
                      >
                        <div className="text-muted-foreground w-16 text-sm">
                          {label}
                        </div>
                        <div className="flex min-w-0 flex-wrap items-center gap-3 sm:gap-4">
                          <label className="inline-flex flex-none items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={openingHours[key].open}
                              onChange={(e) => {
                                const next = {
                                  ...openingHours,
                                  [key]: {
                                    ...openingHours[key],
                                    open: e.target.checked,
                                  },
                                }
                                syncOpeningHours(next)
                              }}
                            />
                            Abierto
                          </label>
                          <Input
                            type="time"
                            value={openingHours[key].start}
                            onChange={(e) => {
                              const next = {
                                ...openingHours,
                                [key]: {
                                  ...openingHours[key],
                                  start: e.target.value,
                                },
                              }
                              syncOpeningHours(next)
                            }}
                            disabled={!openingHours[key].open}
                            className="border-border focus:border-primary h-9 w-24 flex-none border-2 text-sm transition-colors duration-200 sm:w-28"
                          />
                          <span className="text-muted-foreground text-sm">
                            a
                          </span>
                          <Input
                            type="time"
                            value={openingHours[key].end}
                            onChange={(e) => {
                              const next = {
                                ...openingHours,
                                [key]: {
                                  ...openingHours[key],
                                  end: e.target.value,
                                },
                              }
                              syncOpeningHours(next)
                            }}
                            disabled={!openingHours[key].open}
                            className="border-border focus:border-primary h-9 w-24 flex-none border-2 text-sm transition-colors duration-200 sm:w-28"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  {err.opening_hours && (
                    <p className="text-destructive mt-1 text-sm">
                      {err.opening_hours}
                    </p>
                  )}
                </div>
              </div>

              {/* Delivery Coverage Selector */}
              <div>
                <Label className="text-muted-foreground mb-2 block text-sm font-medium">
                  Cobertura de delivery
                </Label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={
                        coverageMode === "neighborhoods" ? "default" : "outline"
                      }
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        setCoverageMode("neighborhoods")
                        syncCoverage(
                          "neighborhoods",
                          coverageNeighborhoods,
                          coverageRadiusKm,
                        )
                      }}
                    >
                      Barrios
                    </Button>
                    <Button
                      type="button"
                      variant={
                        coverageMode === "radius" ? "default" : "outline"
                      }
                      size="sm"
                      className="h-8"
                      onClick={() => {
                        setCoverageMode("radius")
                        syncCoverage(
                          "radius",
                          coverageNeighborhoods,
                          coverageRadiusKm,
                        )
                      }}
                    >
                      Radio (km)
                    </Button>
                  </div>

                  {coverageMode === "neighborhoods" && (
                    <div className="border-border max-h-44 overflow-auto rounded-lg border-2 p-3">
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {neighborhoods.map((n) => {
                          const id = String(n.id)
                          const checked = coverageNeighborhoods.includes(id)
                          return (
                            <label
                              key={id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...coverageNeighborhoods, id]
                                    : coverageNeighborhoods.filter(
                                        (x) => x !== id,
                                      )
                                  setCoverageNeighborhoods(next)
                                  syncCoverage(
                                    "neighborhoods",
                                    next,
                                    coverageRadiusKm,
                                  )
                                }}
                              />
                              {n.name}
                            </label>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {coverageMode === "radius" && (
                    <div className="flex items-center gap-3">
                      <Input
                        type="number"
                        min={0}
                        step={0.5}
                        value={coverageRadiusKm}
                        onChange={(e) => {
                          setCoverageRadiusKm(e.target.value)
                          syncCoverage(
                            "radius",
                            coverageNeighborhoods,
                            e.target.value,
                          )
                        }}
                        className="border-border focus:border-primary h-12 w-28 border-2 text-base transition-colors duration-200"
                      />
                      <span className="text-muted-foreground text-sm">
                        kilómetros desde la dirección del negocio
                      </span>
                    </div>
                  )}

                  {err.delivery_coverage && (
                    <p className="text-destructive mt-1 text-sm">
                      {err.delivery_coverage}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-border flex justify-end gap-4 border-t pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="h-9 bg-transparent px-4 text-sm font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={processing}
              className="h-9 px-4 text-sm font-medium"
            >
              {processing ? "Guardando..." : submitLabel}
            </Button>
          </div>
        </form>
      </CardContent>
    </div>
  )
}
